import React, { useCallback, useMemo, useState } from 'react';
import {
  Collapse,
  FormControl, FormHelperText,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  InputLabel, Paper, Tab, Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTextSlash } from '@fortawesome/free-solid-svg-icons';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { CommandPanels, EditorControls, withBaseEditor } from '../../BaseEditor';
import ROwnerTablePart from './tp/realtyowner';
import RHistoryTablePart from './tp/realtyhistory';

function RealtyEditor({
  data,
  getControl,
  currentUser,
  fields,
  dataSetter,
  erroredFields,
}) {
  const [tab, setTab] = useState('general');
  const onOwnerSetChange = useCallback(
    (e, newRecs) => dataSetter({ realtyownerrelation_set: newRecs }),
    [dataSetter],
  );
  const onHistorySetChange = useCallback(
    (e, newRecs) => dataSetter({ realtyhistory_set: newRecs }),
    [dataSetter],
  );
  const currentOrg = useMemo(
    () => (currentUser.is_superuser ? data.org && (data.org.id || null) : currentUser.org.id),
    [currentUser.is_superuser, currentUser.org.id, data.org],
  );
  const parentFilter = useMemo(
    () => ({
      org: currentOrg,
    }),
    [currentOrg],
  );
  const [addrEditMode, setAddrEditMode] = useState(1);
  const localityAreaFilter = useMemo(
    () => ({ locality: data.locality && data.locality.id }),
    [data.locality],
  );
  const streetFilter = useMemo(
    () => ({ locality: data.locality && data.locality.id }),
    [data.locality],
  );
  const houseFilter = useMemo(
    () => ({ street: data.street && data.street.id }),
    [data.street],
  );
  const onParentChange = useCallback(
    (e, v) => {
      if (v) {
        const {
        // eslint-disable-next-line camelcase
          address, locality, locality_area, street, house,
        } = v;
        dataSetter({
        // eslint-disable-next-line camelcase
          parent: v, address, locality, locality_area, street, house,
        });
      } else {
        dataSetter({ parent: v });
      }
    },
    [dataSetter],
  );

  console.log('data', data);
  return (
    <TabContext value={tab}>
      <TabList onChange={(e, v) => setTab(v)} textColor="primary" indicatorColor="primary">
        <Tab value="general" label="Загальні відомості" />
        <Tab value="detail" label="Детальні відомості" />
        <Tab value="owners" label="Власники" disabled={!data.id} />
      </TabList>
      <TabPanel value="general" sx={{ overflow: 'auto' }}>
        <Grid container spacing={2}>
          <Grid item sm={10}>
            <Collapse in={addrEditMode === 1}>
              <FormControl fullWidth margin="dense">
                <InputLabel
                  htmlFor="addr_input"
                  error={!!erroredFields.address}
                >
                  {fields.address.label}
                </InputLabel>
                <Input
                  id="addr_input"
                  value={data.address}
                  onChange={(e) => dataSetter({ address: e.target.value })}
                  error={!!erroredFields.address}
              // multiline
              // rows={3}
                  margin="dense"
                  endAdornment={(
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle to address classifier"
                        onClick={() => setAddrEditMode(2)}
                      >
                        <FontAwesomeIcon icon={faEdit} size="xs" />
                      </IconButton>
                    </InputAdornment>
              )}
                />
                <FormHelperText error>{erroredFields.address}</FormHelperText>
              </FormControl>
            </Collapse>
            <Collapse in={addrEditMode === 2}>
              <Paper sx={{
                display: 'flex',
                px: 2,
                py: 1,
                gap: 1,
              }}
              >
                {getControl('locality')}
                {getControl('locality_area', { filter: localityAreaFilter })}
                {getControl('street', { filter: streetFilter })}
                {getControl('house', { filter: houseFilter })}
                <IconButton
                  aria-label="toggle to address classifier"
                  onClick={() => setAddrEditMode(1)}
                  edge="end"
                >
                  <FontAwesomeIcon icon={faTextSlash} size="xs" />
                </IconButton>
              </Paper>
            </Collapse>
          </Grid>
          <Grid item sm={2}>
            {getControl('number')}
          </Grid>
          <Grid item sm={4}>
            {getControl('area')}
          </Grid>
          <Grid item sm={4}>
            {getControl('purpose')}
          </Grid>
          <Grid item sm={4}>
            {getControl('realty_type')}
          </Grid>
          <Grid item sm={4}>
            {getControl('parent', { filter: parentFilter, onChange: onParentChange, canErase: true })}
          </Grid>
          <Grid item sm={4}>
            {getControl('registration_status')}
          </Grid>
          <Grid item sm={4}>
            {getControl('state_property')}
          </Grid>
          <Grid item sm={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ color: 'primary.main' }}>
                Вартісні показники майна
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={3}>
                  {getControl('inventory_number')}
                </Grid>
                <Grid item sm={3}>
                  {getControl('initial_value')}
                </Grid>
                <Grid item sm={3}>
                  {getControl('residual_value')}
                </Grid>
                <Grid item sm={3}>
                  {getControl('event_date')}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item sm={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ color: 'primary.main' }}>
                Дані про державу реєстрацію права власності на нерухоме майно
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={6}>
                  {getControl('document_name')}
                </Grid>
                <Grid item sm={6}>
                  {getControl('authority_name')}
                </Grid>
                <Grid item sm={4}>
                  {getControl('registration_date')}
                </Grid>
                <Grid item sm={4}>
                  {getControl('ownership_number')}
                </Grid>
                <Grid item sm={4}>
                  {getControl('registration_number')}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item sm={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ color: 'primary.main' }}>
                Опис нерухомого майна
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={1}>
                  {getControl('year_biulding')}
                </Grid>
                <Grid item sm={3}>
                  {getControl('realy_state')}
                </Grid>
                <Grid item sm={4}>
                  {getControl('wall_material')}
                </Grid>
                <Grid item sm={4}>
                  {getControl('roof_material')}
                </Grid>

                <Grid item sm={1}>
                  {getControl('comfort_water')}
                </Grid>
                <Grid item sm={2}>
                  {getControl('comfort_hot_water')}
                </Grid>
                <Grid item sm={2}>
                  {getControl('comfort_bathroom')}
                </Grid>
                <Grid item sm={2}>
                  {getControl('comfort_wc')}
                </Grid>
                <Grid item sm={2}>
                  {getControl('comfort_hot')}
                </Grid>
                <Grid item sm={1}>
                  {getControl('comfort_gas')}
                </Grid>
                <Grid item sm={2}>
                  {getControl('comfort_press_gas')}
                </Grid>

                <Grid item sm={12}>
                  <EditorControls.TextInput
                    label={fields.description.label}
                    // readOnly={fields.description.read_only}
                    value={data.description}
                    onChange={(e, v) => dataSetter({ description: v })}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {currentUser.is_superuser && (
          <Grid item sm={12}>
            {getControl('org', {
              backend: '/api/catalog/orgs/',
            })}
          </Grid>
          )}
        </Grid>
      </TabPanel>
      <TabPanel value="detail" sx={{ overflow: 'auto' }}>
        <RHistoryTablePart
          data={data.realtyhistory_set}
          options={fields.realtyhistory_set}
          onChange={onHistorySetChange}
          errors={erroredFields.realtyhistory_set}
          // currentOrg={currentOrg}
        />
      </TabPanel>
      <TabPanel value="owners" sx={{ overflow: 'auto' }}>
        <ROwnerTablePart
          data={data.realtyownerrelation_set}
          options={fields.realtyownerrelation_set}
          onChange={onOwnerSetChange}
          errors={erroredFields.realtyownerrelation_set}
          currentOrg={currentOrg}
        />
      </TabPanel>
    </TabContext>
  );
}

RealtyEditor.propTypes = {
  getControl: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number,
    org: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    locality: PropTypes.shape({
      id: PropTypes.number,
      koatuu: PropTypes.string,
    }),
    locality_area: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    street: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    house: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    address: PropTypes.string,
    description: PropTypes.string,
    realtyownerrelation_set: PropTypes.arrayOf(PropTypes.shape({})),
    realtyhistory_set: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  currentUser: PropTypes.shape({
    is_superuser: PropTypes.bool,
    org: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
  }).isRequired,
  dataSetter: PropTypes.func.isRequired,
  fields: PropTypes.shape({
    description: PropTypes.shape({
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      label: PropTypes.string,
    }),
    address: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      max_length: PropTypes.number,
    }),
    realtyownerrelation_set: PropTypes.shape({
      label: PropTypes.string,
    }),
    realtyhistory_set: PropTypes.shape({
      label: PropTypes.string,
    }),
  }).isRequired,
  erroredFields: PropTypes.shape({
    address: PropTypes.arrayOf(PropTypes.string),
    realtyownerrelation_set: PropTypes.arrayOf(PropTypes.shape),
    realtyhistory_set: PropTypes.arrayOf(PropTypes.shape),
  }).isRequired,
};

export default withBaseEditor(
  RealtyEditor,
  '/api/realty/realty/',
  CommandPanels.catCommandPanel,
);
